import React, { useEffect, useState, ReactNode } from 'react';
import { Input } from "@/ui-components/ui/input";
import { Label } from "@/ui-components/ui/label";
import { Edit3, Type, Tag } from "lucide-react";
import { toast } from "@/lib/toast";
import { Template } from "@/page-components/post-generator/core/template.schemas";
import { useTemplates2GetCategories, useTemplates2Update } from "@/lib/api/requests/templates.requests";
import { CategorySelector } from '@/page-components/post-generator/components/CategorySelector';
import { CreateBaseDialog } from "@/util-components/dialog/base-dialogs/create-base-dialog";

interface EditTemplateDialogProps {
  trigger?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  template: Template;
  onSuccess?: () => void;
}

export const EditTemplateDialog: React.FC<EditTemplateDialogProps> = ({
  trigger,
  open: externalOpen,
  onOpenChange,
  template,
  onSuccess,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const setOpen = onOpenChange || setInternalOpen;

  const categoriesQuery = useTemplates2GetCategories({ enabled: !!open });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const categories = categoriesQuery.data?.categories ?? [];

  const updateMutation = useTemplates2Update();

  useEffect(() => {
    if (open) {
      setNewTitle(template.title || "");
      setSelectedCategories(template.meta?.categories || []);
    } else {
      setNewTitle("");
      setSelectedCategories([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!newTitle.trim()) {
      toast.error('Name erforderlich', {
        description: 'Bitte geben Sie einen Namen für das Template ein.',
        duration: 3000,
      });
      return;
    }

    // Check for changes
    const currentCategories = template.meta?.categories || [];
    
    const categoriesChanged = 
      selectedCategories.length !== currentCategories.length ||
      !selectedCategories.every(cat => currentCategories.includes(cat));

    if (newTitle.trim() === template.title && !categoriesChanged) {
      toast.info('Keine Änderung', {
        description: 'Name und Kategorie sind identisch mit den aktuellen Werten.',
        duration: 3000,
      });
      setOpen(false);
      return;
    }

    try {
      const payload: any = { title: newTitle.trim() };
      // Update with new categories array
      payload.meta = { 
        categories: selectedCategories
      };
            
      await updateMutation.mutateAsync({ id: template.id, payload });
      toast.success('Template aktualisiert', {
        description: 'Name und Kategorien wurden geändert.',
        duration: 3000,
      });
      setOpen(false);
      onSuccess?.();
    } catch (error) {
      toast.error('Fehler beim Bearbeiten', {
        description: 'Das Template konnte nicht bearbeitet werden. Bitte versuchen Sie es erneut.',
        duration: 5000,
        error,
      });
    }
  };

  const isLoading = updateMutation.isPending;

  const originalTitle = template.title || "";
  const currentCategories = template.meta?.categories || [];

  const hasChanges = 
    newTitle.trim() !== originalTitle ||
    selectedCategories.length !== currentCategories.length ||
    !selectedCategories.every(cat => currentCategories.includes(cat));

  return (
    <>
      {trigger && (
        <div onClick={() => setOpen(true)} className="inline-block">
          {trigger}
        </div>
      )}
      <CreateBaseDialog
        open={open}
        onClose={() => setOpen(false)}
        icon={Edit3}
        title="Template bearbeiten"
        description="Name und Kategorie ändern"
        isSaving={isLoading}
        canSave={!!newTitle.trim() && hasChanges}
        formId="edit-template-form"
        submitLabel="Bearbeiten"
        submitLoadingLabel="Bearbeiten..."
        maxWidth="lg"
      >
        <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-5 sm:pb-6 pt-2">
          <form id="edit-template-form" onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Type className="h-4 w-4 text-gray-500" />
                <Label htmlFor="newTitle" className="text-sm font-medium text-gray-700">Name</Label>
                <span className="text-xs text-red-500">*</span>
              </div>
              <Input
                id="newTitle"
                placeholder="Template-Name..."
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                disabled={isLoading}
                className="h-11 border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 transition-all"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-gray-500" />
                <Label className="text-sm font-medium text-gray-700">Kategorie</Label>
              </div>
              <CategorySelector
                categories={categories}
                selected={selectedCategories}
                onChange={setSelectedCategories}
                isLoading={isLoading}
                mode="multiple"
                allowCreate={true}
                placeholder="Kategorien wählen oder erstellen..."
              />
            </div>
          </form>
        </div>
      </CreateBaseDialog>
    </>
  );
};
